import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { Square } from "lucide-react";
import { SONIOX_VOICES } from "../lib/schemaModes";
import { createDefusionPlayer, DefusionVariant } from "../lib/defusionPlayer";
import { postChair } from "../api";
import { useToast } from "../../../components/Toast";

interface DefusionVoiceToolProps {
  patientId: string;
  sessionId?: string;
  language: "en" | "hu";
  getToken: () => Promise<string | null>;
}

const VARIANTS: DefusionVariant[] = ["plain", "slow", "fast", "far"];

/* ACT cognitive defusion: the sticky thought synthesized once, then re-played
   manipulated — slowed, sped up, drifting far away — until the words stop
   sounding like the truth. */
export function DefusionVoiceTool({ patientId, sessionId, language, getToken }: DefusionVoiceToolProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [text, setText] = useState("");
  const [voice, setVoice] = useState("Mason");
  const [busy, setBusy] = useState(false);
  const [generated, setGenerated] = useState(false);

  const playerRef = useRef<ReturnType<typeof createDefusionPlayer> | null>(null);

  const onGenerate = async () => {
    try {
      setBusy(true);
      await playerRef.current?.close();
      const res = await postChair(getToken, {
        patient_id: patientId,
        session_id: sessionId,
        language,
        chairs: [{ id: crypto.randomUUID(), mode: "defusion_voice", label: "", text, voice }],
      });
      const player = createDefusionPlayer();
      const data = await fetch(res.clips[0].url).then((r) => r.arrayBuffer());
      await player.load(data);
      playerRef.current = player;
      setGenerated(true);
    } catch (e) {
      toast.error(`${t("experiential.genError")}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    return () => {
      playerRef.current?.close();
    };
  }, []);

  return (
    <div>
      <TextField
        label={t("experiential.defusionText")}
        multiline
        minRows={2}
        fullWidth
        size="small"
        value={text}
        onChange={(e) => setText(e.target.value)}
        sx={{ mb: 2 }}
      />
      <FormControl size="small" sx={{ minWidth: 130, mb: 2 }}>
        <InputLabel>{t("experiential.voice")}</InputLabel>
        <Select value={voice} label={t("experiential.voice")} onChange={(e) => setVoice(e.target.value)}>
          {SONIOX_VOICES.map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className="exp-chair-controls">
        <Button variant="contained" size="small" onClick={onGenerate} disabled={busy || !text.trim()}>
          {t("experiential.generate")}
        </Button>

        {generated && (
          <>
            {VARIANTS.map((variant) => (
              <Button
                key={variant}
                size="small"
                variant="outlined"
                onClick={() => playerRef.current?.play(variant)}
              >
                {t(`experiential.defusionVariants.${variant}`)}
              </Button>
            ))}
            <button
              type="button"
              onClick={() => playerRef.current?.stop()}
              aria-label={t("experiential.stop")}
              title={t("experiential.stop")}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "inherit" }}
            >
              <Square size={18} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
